"use client";
import React from "react";
import {
  DayPicker,
  CaptionProps,
  useNavigation,
  DateFormatter,
  DayPickerSingleProps,
} from "react-day-picker";
import "react-day-picker/dist/style.css";
import "@/styles/date-picker.css";
import Select from "./select";

import Icon from "./icon";
import Popover from "./popover";
import { BUTTON_VARIANCE } from "./button";

interface DatePickerProps extends DayPickerSingleProps {
  value?: Date;
  onValueChange?: (v: Date) => void;
  align?: "start" | "center" | "end";
}

function DatePicker(props: DatePickerProps) {
  const selectOpenRef = React.useRef(false);
  const [selectedDay, setSelectedDay] = React.useState<Date | undefined>(
    props.value
  );
  const [open, setOpen] = React.useState(false);

  const handleDayClick = (day: Date) => {
    setSelectedDay(day);
    if (props.onValueChange) {
      props.onValueChange(day);
    }
    setOpen(false);
  };

  React.useEffect(() => {
    setSelectedDay(props.value);
  }, [props.value]);

  const formatMonthCaption: DateFormatter = (date, options) => {
    const monthNumber = date.getMonth();
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return monthNames[monthNumber];
  };
  const formatWeekdayName: DateFormatter = (date, options) => {
    const dayNumber = date.getDay();
    const weekdayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return weekdayNames[dayNumber];
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Trigger></Trigger>
      <Popover.Content
        sideOffset={-8}
        side="bottom"
        avoidCollisions={false}
        onInteractOutside={(e) => {
          if (selectOpenRef.current) {
            e.preventDefault();
          }
        }}
        align={props.align}
      >
        <DayPicker
          {...props}
          mode="single"
          selected={selectedDay}
          onDayClick={(v) => handleDayClick(v)}
          showOutsideDays
          fixedWeeks
          defaultMonth={selectedDay}
          formatters={{ formatWeekdayName, formatMonthCaption }}
          components={{
            Caption: (props) => (
              <CustomCaption
                {...props}
                fromYear={2020}
                toYear={new Date().getFullYear()}
                selectOpenRef={selectOpenRef}
              />
            ),
          }}
        />
      </Popover.Content>
    </Popover>
  );
}
interface CustomCaptionProps extends CaptionProps {
  fromYear: number;
  toYear: number;
  selectOpenRef: React.MutableRefObject<boolean>;
}

function CustomCaption(props: CustomCaptionProps) {
  const { goToMonth } = useNavigation();

  const month = props.displayMonth.getMonth();
  const year = props.displayMonth.getFullYear();

  const { displayMonth, fromYear, toYear } = props;

  function generateMonths() {
    const monthNames = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];

    return Array.from({ length: 12 }, (_, index) => ({
      value: index.toString(),
      label: monthNames[index],
    }));
  }
  interface YearOption {
    value: string;
    label: string;
  }

  function generateYears(fromYear: number, toYear: number): YearOption[] {
    return Array.from({ length: toYear - fromYear + 1 }, (_, index) => {
      const yearValue = fromYear + index;
      return {
        value: yearValue.toString(),
        label: yearValue.toString(),
      };
    });
  }

  const monthOptions = generateMonths();
  const yearOptions = generateYears(fromYear, toYear);

  const handleMonthChange = (value: string) => {
    const newMonthIndex = parseInt(value, 10);
    const newDate = new Date(year, newMonthIndex);
    goToMonth(newDate);
  };

  const handleYearChange = (value: string) => {
    const newYear = parseInt(value, 10);
    const newDate = new Date(newYear, month);
    goToMonth(newDate);
  };

  const isPrevMonthDisabled =
    displayMonth.getFullYear() <= fromYear && displayMonth.getMonth() === 0;
  const isNextMonthDisabled =
    displayMonth.getFullYear() >= toYear && displayMonth.getMonth() === 11;

  const handlePrevMonthClick = () => {
    const prevMonth = new Date(
      displayMonth.getFullYear(),
      displayMonth.getMonth() - 1
    );
    goToMonth(prevMonth);
  };

  const handleNextMonthClick = () => {
    const nextMonth = new Date(
      displayMonth.getFullYear(),
      displayMonth.getMonth() + 1
    );
    goToMonth(nextMonth);
  };

  return (
    <>
      <div className="rdp-caption">
        <div className="rdp-nav">
          <button
            className={`rdp-nav_button_previous ${BUTTON_VARIANCE.base} ${BUTTON_VARIANCE.circle.base} ${BUTTON_VARIANCE.circle.size.xs} ${BUTTON_VARIANCE.standard.color.filled.secondary} disabled:bg-surface-600 disabled:fill-secondary disabled:cursor-not-allowed`}
            onClick={handlePrevMonthClick}
            aria-label="Previous Month"
            disabled={isPrevMonthDisabled}
          >
            <Icon name="caret-left" size="sm" />
          </button>
          <button
            className={`rdp-nav_button_next ${BUTTON_VARIANCE.base} ${BUTTON_VARIANCE.circle.base} ${BUTTON_VARIANCE.circle.size.xs} ${BUTTON_VARIANCE.standard.color.filled.secondary} disabled:bg-surface-600 disabled:fill-secondary disabled:cursor-not-allowed`}
            onClick={handleNextMonthClick}
            aria-label="Next Month"
            disabled={isNextMonthDisabled}
          >
            <Icon name="caret-right" size="sm" />
          </button>
        </div>
        <Select
          variant="card"
          height={16}
          value={month.toString()}
          onValueChange={handleMonthChange}
          options={monthOptions}
          label="Select Month"
          setSelecting={(isOpen: boolean) => {
            props.selectOpenRef.current = isOpen;
          }}
        ></Select>
        <Select
          variant="card"
          height={16}
          value={year.toString()}
          onValueChange={handleYearChange}
          options={yearOptions.reverse()}
          label="Select Month"
          setSelecting={(isOpen: boolean) => {
            props.selectOpenRef.current = isOpen;
          }}
        ></Select>
      </div>
    </>
  );
}

function Trigger() {
  return (
    <Popover.Trigger
      className={`${BUTTON_VARIANCE.base} ${BUTTON_VARIANCE.standard.base} ${BUTTON_VARIANCE.standard.size.md} ${BUTTON_VARIANCE.standard.color.filled.secondary} `}
    >
      <Icon name="calendar" className="fill-dark" size="lg" />
    </Popover.Trigger>
  );
}

DatePicker.Trigger = Trigger;
export default DatePicker;
